import { BaseScanGridWidget } from './BaseScanGridWidget';
import { getLabelStyle } from './widgetLabelHelpers';
import { evaluateThreshold } from '../../utils/thresholds';

/**
 * Extract Infrastructure Findings specifically for the Advanced Summary
 * 
 * Infrastructure – Scan Definitions
 * 1. Server OS Support Status
 * 2. Hardware Warranty & Lifecycle
 * 3. Single Points of Failure
 * 4. Capacity Headroom
 * 5. Virtualization Host Health
 * 6. Network Core Health
 * 7. Configuration Standardization
 * 8. Cloud Resource Health
 * 9. Infrastructure Growth Readiness
 */
export const DEFAULT_INFRASTRUCTURE_CATEGORIES = [
  { id: 'infrastructure-1', label: 'Server OS Support Status' },
  { id: 'infrastructure-2', label: 'Average Workstation Age' },
  { id: 'infrastructure-3', label: 'Single Points of Failure' },
  { id: 'infrastructure-4', label: 'Capacity Headroom' },
  { id: 'infrastructure-5', label: 'Virtualization Host Health' },
  { id: 'infrastructure-6', label: 'Network Core Health' },
  { id: 'infrastructure-7', label: 'Configuration Standardization' },
  { id: 'infrastructure-8', label: 'Cloud Resource Health' },
  { id: 'infrastructure-9', label: 'Infrastructure Growth Readiness' }
];

// Extra auto source options that can be selected in the Source Logic dropdown
// but are NOT displayed as default grid items
export const EXTRA_AUTO_SOURCES = [
  { id: 'infrastructure-hw-warranty', label: 'Hardware Warranty & Lifecycle' }
];

export const extractInfrastructureFindings = (data, config = {}) => {
  // Handle different data structures robustly
  const scanData = data?.scanData || data?.data || data || {};
  
  // Ensure we have an object to work with
  const actualScanData = (typeof scanData === 'object' && !Array.isArray(scanData)) ? scanData : {};
  
  const localhost = actualScanData.localhost || actualScanData.System || {};
  const networkDevices = Array.isArray(actualScanData.network_devices) ? actualScanData.network_devices : [];
  const adData = actualScanData['Active Directory Scan'] || actualScanData['AD Scan'] || actualScanData['Ad Scan'] || {};
  const dataOverrides = config.dataOverrides || {};

  const findings = [];

  // Helper to create finding object
  const createFinding = (id, title, status, score, description, value = null, metrics = []) => ({
    id, title, status, score, description, value, metrics, category: 'Infrastructure'
  });

  // Helper for flexible scoring (Score = 0-100 Health, Value = Raw Metric)
  const evaluateStatusAndScore = (value, total, id, defaultMode = 'percentage') => {
    // 1. Check for dynamic category thresholds from backend
    const categoryName = DEFAULT_INFRASTRUCTURE_CATEGORIES.find(c => c.id === id)?.label || 
                         EXTRA_AUTO_SOURCES.find(c => c.id === id)?.label;
    
    const dynamicThresholds = actualScanData.category_thresholds?.[categoryName];

    if (dynamicThresholds && dynamicThresholds.rules) {
      const tier = evaluateThreshold(value, 'numeric', dynamicThresholds.rules); // Most of these are numeric
      const statusMap = { 'Good': 'Green', 'Moderate': 'Yellow', 'Bad': 'Red' };
      const scoreMap = { 'Good': 100, 'Moderate': 50, 'Bad': 0 };
      
      if (tier && tier !== 'Unknown') {
        return { 
          status: statusMap[tier] || 'Unknown', 
          score: scoreMap[tier] || 0, 
          value, 
          mode: 'dynamic' 
        };
      }
    }

    // 2. Fallback to existing hardcoded logic
    const override = dataOverrides[id] || {};
    const mode = override.scoringMode || defaultMode;
    // Default thresholds depend on mode if not specified
    const defaultGreen = mode === 'count_max' ? 0 : 90;
    const defaultYellow = mode === 'count_max' ? 1 : 70;
    
    const greenThreshold = override.greenThreshold !== undefined ? override.greenThreshold : defaultGreen;
    const yellowThreshold = override.yellowThreshold !== undefined ? override.yellowThreshold : defaultYellow;

    let score = 0; // Health/Satisfaction (0-100)
    let status = 'Unknown';

    if (mode === 'percentage') {
      const calcScore = total > 0 ? (value / total) * 100 : 0;
      score = Number.isInteger(calcScore) ? calcScore : parseFloat(calcScore.toFixed(2));
      if (score >= greenThreshold) status = 'Green';
      else if (score >= yellowThreshold) status = 'Yellow';
      else status = 'Red';
    } else if (mode === 'count_min') {
      // Higher is better
      if (value >= greenThreshold) status = 'Green';
      else if (value >= yellowThreshold) status = 'Yellow';
      else status = 'Red';
      
      // Map to health score
      if (status === 'Green') score = 100;
      else if (status === 'Yellow') score = 50;
      else score = 0;
    } else if (mode === 'count_max') {
      // Lower is better
      if (value <= greenThreshold) status = 'Green';
      else if (value <= yellowThreshold) status = 'Yellow';
      else status = 'Red';
      
      // Map to health score
      if (status === 'Green') score = 100;
      else if (status === 'Yellow') score = 50;
      else score = 0;
    }

    return { status, score, value, mode };
  };

  // 1. Server OS Support Status
  let totalServers = 0;
  let supportedServers = 0;
  let osDataFound = false;
  
  // Check localhost
  const hardwareInfo = localhost['Hardware Informations'] || localhost.System || localhost || localhost['Hardware Informations'];
  const localOS = hardwareInfo?.OS || hardwareInfo?.["OS Version"] || localhost.OS || localhost["OS Version"];
  
  if (localOS) {
    if (localOS.toLowerCase().includes("server")) {
        osDataFound = true;
        totalServers++;
        if (!localOS.includes("2008") && !localOS.includes("2003") && !localOS.includes("2012")) {
            supportedServers++;
        }
    }
  }

  // Check network devices
  if (Array.isArray(networkDevices) && networkDevices.length > 0) {
    networkDevices.forEach(device => {
       const deviceSystem = device.system || {};
       const deviceOS = deviceSystem.OS || device.os_name || device.os || "";
       if (deviceOS && deviceOS !== "WMI Error") {
         if (deviceOS.toLowerCase().includes("server")) {
            osDataFound = true;
            totalServers++;
            if (!deviceOS.includes("2008") && !deviceOS.includes("2003") && !deviceOS.includes("2012")) {
                supportedServers++;
            }
         }
       }
    });
  }

  // 1. Server OS Support Status
  let osStatus = 'Unknown';
  let osScore = 0;
  let osDesc = 'OS version information not available';

  if (osDataFound && totalServers > 0) {
    const evalResult = evaluateStatusAndScore(supportedServers, totalServers, 'infrastructure-1');
    osStatus = evalResult.status;
    osScore = evalResult.score;
    osDesc = evalResult.mode === 'percentage' 
      ? `${osScore}% of servers (${supportedServers}/${totalServers}) running supported OS versions`
      : `${supportedServers} supported servers found (Total: ${totalServers})`;
  } else if (osDataFound) {
      osStatus = 'Unknown';
      osDesc = 'No server OS versions identified in scan';
  }

  findings.push(createFinding(
    'infrastructure-1', 
    'Server OS Support Status', 
    osStatus, 
    osScore, 
    osDesc,
    supportedServers, // value
    [{ label: 'Supported', value: supportedServers }, { label: 'Total', value: totalServers }] // metrics
  ));


  // 2. Average Workstation Age
  let ageStatus = 'Unknown';
  let ageScore = 0;
  let ageDesc = 'Workstation age data not available';
  let ageMetrics = [];

  let goodCount = 0; // For Age calculation
  const adComputers = adData?.Computers || adData?.computers || [];
  if (Array.isArray(adComputers) && adComputers.length > 0) {
    const workstations = adComputers.filter(c => {
      const flags = c.account_flags || [];
      const isWorkstationAccount = flags.includes('WORKSTATION_TRUST_ACCOUNT');
      const osName = (c.os || '').toLowerCase();
      const isNotServer = !osName.includes('server');
      return isWorkstationAccount && isNotServer;
    });

    const now = new Date();
    let knownCount = 0;

    workstations.forEach(ws => {
      if (ws.created) {
        const createdDate = new Date(ws.created);
        if (!isNaN(createdDate.getTime())) {
          const ageYears = (now - createdDate) / (365.25 * 24 * 60 * 60 * 1000);
          knownCount++;
          if (ageYears <= 3) {
            goodCount++;
          }
        }
      }
    });

    if (workstations.length === 0) {
      ageDesc = 'No workstations found in AD';
    } else if (knownCount === 0) {
      ageDesc = 'Workstations found but age unknown';
    } else {
      const evalResult = evaluateStatusAndScore(goodCount, knownCount, 'infrastructure-2');
      ageStatus = evalResult.status;

      if (evalResult.mode === 'percentage') {
         ageScore = evalResult.score;
         ageDesc = `${ageScore}% of workstations are ≤ 3 years old (${goodCount}/${knownCount})`;
      } else {
         ageScore = goodCount;
         ageDesc = `${goodCount} workstations are ≤ 3 years old`;
      }
    }

    ageMetrics = [
      { label: '≤3yr', value: goodCount },
      { label: 'Total', value: knownCount }
    ];
  }

  findings.push(createFinding(
    'infrastructure-2',
    'Average Workstation Age',
    ageStatus,
    ageScore,
    ageDesc,
    goodCount, // value
    ageMetrics
  ));


  // 3. Single Points of Failure
  // Logic: 1 DC = 0%, 2+ DC = 100%
  let redundancyStatus = 'Unknown';
  let redundancyScore = 0;
  let redundancyDesc = 'Redundancy configuration not found';
  let dcCount = 0;

  const dcData = adData?.['Domain Controllers'] || adData?.['domain_controllers'] || null;
  if (dcData) {
      dcCount = Array.isArray(dcData) ? dcData.length : 0;
      
      // Determine health (could use count mode, but here we calculate a base percentage for evaluateStatusAndScore)
      const healthVal = dcCount > 1 ? 100 : (dcCount === 1 ? 0 : 0);
      const evalResult = evaluateStatusAndScore(healthVal, 100, 'infrastructure-3');
      
      redundancyStatus = evalResult.status;
      redundancyScore = evalResult.score;
      redundancyDesc = dcCount > 1 
        ? `Redundancy ok: ${dcCount} Domain Controllers detected`
        : (dcCount === 1 ? 'Single Point of Failure: Only 1 Domain Controller detected' : 'No Domain Controllers detected');
  }

  findings.push(createFinding(
    'infrastructure-3',
    'Single Points of Failure',
    redundancyStatus,
    redundancyScore,
    redundancyDesc,
    dcCount, // value
    [{ label: 'Domain Controllers', value: dcCount }]
  ));


  // 4. Capacity Headroom
  // Logic: % of disks with < 90% usage
  let capStatus = 'Unknown';
  let capScore = 0;
  let capDesc = 'Performance metrics not available';

  const logicalDisks = hardwareInfo?.['Logical Disks'] || localhost['Logical Disks'] || localhost.Storage?.Drives || localhost.Drives || [];
  let healthyDisks = 0;
  
  if (logicalDisks.length > 0) {
      let totalDisks = 0;
      
      if (Array.isArray(logicalDisks)) {
          logicalDisks.forEach(disk => {
              const percentUsed = disk['Percent Used'];
              if (percentUsed) {
                  totalDisks++;
                  const usage = parseFloat(percentUsed.replace('%', ''));
                  if (usage < 90) {
                      healthyDisks++;
                  }
              }
          });
      }

      if (totalDisks > 0) {
        const evalResult = evaluateStatusAndScore(healthyDisks, totalDisks, 'infrastructure-4');
        capStatus = evalResult.status;
        capScore = evalResult.score;
        capDesc = evalResult.mode === 'percentage'
            ? `${capScore}% of disks within capacity limits (${healthyDisks}/${totalDisks})`
            : `${healthyDisks} disks within capacity limits`;
      }
  }

  findings.push(createFinding('infrastructure-4', 'Capacity Headroom', capStatus, capScore, capDesc, healthyDisks));


  // 5. Virtualization Host Health
  // Logic: Binary (detected = 100%, else 0% if we assume scan target IS a host, mostly 100% for presence)
  let virtStatus = 'Unknown';
  let virtScore = 0;
  let virtDesc = 'No virtualization hosts detected';

  const installedSoftware = hardwareInfo?.installed_softwares || localhost.installed_softwares || {};
  let softwareNames = [];
  if (Array.isArray(installedSoftware)) {
      softwareNames = installedSoftware.map(s => String(s?.Name || s?.name || '')).filter(Boolean);
  } else if (typeof installedSoftware === 'object' && installedSoftware !== null) {
      softwareNames = Object.keys(installedSoftware);
  }
  const hasHyperV = softwareNames.some(s => s.includes('Hyper-V'));
  const hasVMware = softwareNames.some(s => s.includes('VMware'));

  if (hasHyperV || hasVMware) {
      const evalResult = evaluateStatusAndScore(100, 100, 'infrastructure-5');
      virtStatus = evalResult.status;
      virtScore = evalResult.score;
      virtDesc = 'Virtualization hosts detected (Hyper-V/VMware)';
  } else {
      const evalResult = evaluateStatusAndScore(0, 100, 'infrastructure-5');
      virtStatus = evalResult.status;
      virtScore = evalResult.score;
      virtDesc = 'No virtualization hosts detected';
  }

  findings.push(createFinding('infrastructure-5', 'Virtualization Host Health', virtStatus, virtScore, virtDesc, (hasHyperV || hasVMware) ? 100 : 0));


  // 6. Network Core Health
  // Logic: > 0 core devices = 100%
  let netStatus = 'Unknown';
  let netScore = 0;
  let netDesc = 'Network core device data not available';

  if (Array.isArray(networkDevices) && networkDevices.length > 0) {
      const coreDevices = networkDevices.filter(d => 
          d.type === 'Firewall' || 
          d.type === 'Switch' || 
          d.type === 'Router' ||
          (d.device_type && (d.device_type.includes('switch') || d.device_type.includes('router') || d.device_type.includes('firewall')))
      );
      
      const evalResult = evaluateStatusAndScore(coreDevices.length > 0 ? 100 : 0, 100, 'infrastructure-6');
      netStatus = evalResult.status;
      netScore = evalResult.score;
      netDesc = coreDevices.length > 0 ? `${coreDevices.length} core network devices online` : 'No core network devices detected';
  } else {
      const evalResult = evaluateStatusAndScore(0, 100, 'infrastructure-6');
      netStatus = evalResult.status;
      netScore = evalResult.score;
      netDesc = 'Network core device data not available';
  }

  findings.push(createFinding('infrastructure-6', 'Network Core Health', netStatus, netScore, netDesc, 0));


  // 7. Configuration Standardization
  // Logic: Firewall/Services checked = 100%
  let configStatus = 'Unknown';
  let configScore = 0;
  let configDesc = 'Configuration baselines not found';
  
  const hasFirewall = hardwareInfo?.Firewall || localhost.Firewall;
  const hasServices = hardwareInfo?.['Non-Default Services'] || localhost['Non-Default Services'];
  
  const evalResult = evaluateStatusAndScore((hasFirewall || hasServices) ? 100 : 0, 100, 'infrastructure-7');
  configStatus = evalResult.status;
  configScore = evalResult.score;
  configDesc = (hasFirewall || hasServices) ? 'Standard configurations applied; minimal drift detected' : 'Configuration baselines not found';

  findings.push(createFinding('infrastructure-7', 'Configuration Standardization', configStatus, configScore, configDesc, (hasFirewall || hasServices) ? 100 : 0));


  // 8. Cloud Resource Health
  // Logic: Data exists = 100%
  let cloudStatus = 'Unknown';
  let cloudScore = 0;
  let cloudDesc = 'Cloud environment not scanned';

  const ms365Data = scanData['MS365 Scan'] || scanData.MS365;
  const cloudEvalResult = evaluateStatusAndScore(ms365Data ? 100 : 0, 100, 'infrastructure-8');
  cloudStatus = cloudEvalResult.status;
  cloudScore = cloudEvalResult.score;
  cloudDesc = ms365Data ? 'Cloud identity resources (MS365) healthy' : 'Cloud environment not scanned';

  findings.push(createFinding('infrastructure-8', 'Cloud Resource Health', cloudStatus, cloudScore, cloudDesc, ms365Data ? 100 : 0));


  // 9. Infrastructure Growth Readiness
  // Logic: Average of Cap/Redundancy/Age scores
  let growthStatus = 'Unknown';
  let growthScore = 0;
  let growthDesc = 'Insufficient data to project growth readiness';

  const growthComponents = [capScore, redundancyScore, ageScore].filter(s => s !== undefined);
  
  if (growthComponents.length > 0) {
      const avgGrowthScore = growthComponents.reduce((a, b) => a + b, 0) / growthComponents.length;
      let rawGrowthScore = Math.round(avgGrowthScore);
      
      const evalResult = evaluateStatusAndScore(rawGrowthScore, 100, 'infrastructure-9');
      growthStatus = evalResult.status;
      growthScore = evalResult.score;

      const override = dataOverrides['infrastructure-9'] || {};
      const gThreshold = override.greenThreshold !== undefined ? override.greenThreshold : 90;
      const yThreshold = override.yellowThreshold !== undefined ? override.yellowThreshold : 70;

      if (rawGrowthScore >= gThreshold) growthDesc = 'Infrastructure positioned for growth';
      else if (rawGrowthScore >= yThreshold) growthDesc = 'Growth limited by potential checks';
      else growthDesc = 'Critical inhibitors to growth detected';
  }

  findings.push(createFinding('infrastructure-9', 'Infrastructure Growth Readiness', growthStatus, growthScore, growthDesc, growthScore));


  // Extra: Hardware Warranty
  let hwStatus = 'Unknown';
  let hwScore = 0;
  let hwDesc = 'Hardware model data not available';
  let hwMetrics = [];

  const hwInfo = localhost?.['Hardware Informations'] || localhost?.['hardware_informations'] || null;
  if (hwInfo) {
    const model = hwInfo.Model || hwInfo.model || '';
    const manufacturer = hwInfo.Manufacturer || hwInfo.manufacturer || '';
    
    if (model || manufacturer) {
      const evalResult = evaluateStatusAndScore(100, 100, 'infrastructure-hw-warranty');
      hwStatus = evalResult.status;
      hwScore = evalResult.score;
      hwDesc = `Physical hardware: ${manufacturer} ${model}`;
      hwMetrics = [
        { label: 'Manufacturer', value: manufacturer || 'N/A' },
        { label: 'Model', value: model || 'N/A' }
      ];
    }
  }

  findings.push(createFinding(
    'infrastructure-hw-warranty',
    'Hardware Warranty & Lifecycle',
    hwStatus,
    hwScore,
    hwDesc,
    100, // value
    hwMetrics
  ));

  return findings;
};

/**
 * Resolve a nested data path from full scan data.
 * e.g. source='network_devices', path=['system','OS'] →  
 *   scanData.network_devices  (array) → each item .system.OS
 */
export const resolveDataPath = (scanData, source, pathSegments = []) => {
  if (!scanData || !source) return undefined;
  let data = scanData[source] ?? scanData?.data?.[source] ?? scanData?.scanData?.[source];
  if (data === undefined) return undefined;
  for (const seg of pathSegments) {
    if (data == null) return undefined;
    if (Array.isArray(data)) {
      // For arrays, drill into each element and collect results
      data = data.map(item => item?.[seg]).filter(v => v !== undefined);
      if (data.length === 0) return undefined;
      // If every element is still an object/array, keep the array
      // If every element is a primitive, keep as value array
      continue;
    }
    data = data[seg];
  }
  return data;
};

/**
 * Get unique values for a field in an array of objects located at source/path.
 */
export const getUniqueValues = (scanData, source, path, field) => {
  const data = resolveDataPath(scanData, source, path);
  if (!Array.isArray(data)) return [];
  // Allow empty strings but map null/undefined to nothing
  const values = data
    .map(item => item?.[field])
    .filter(v => v !== undefined && v !== null)
    .map(v => String(v).trim());
  
  const unique = [...new Set(values)].sort();
  
  // UX Improvement: If field looks boolean (contains only true/false values), show both options
  // even if data only contains one of them (e.g. all false).
  if (unique.length > 0 && unique.every(v => v.toLowerCase() === 'true' || v.toLowerCase() === 'false')) {
    return ['false', 'true'];
  }
  
  return unique;
};

/**
 * Apply a filter to an array of items.
 * filter: { field, operator, value }
 */
const applyFilter = (items, filter) => {
  if (!Array.isArray(items) || !filter?.field) return items;
  const { field, value } = filter;
  // Default operator to 'equals' if not specified (fixes issue where initial filter is ignored)
  const operator = filter.operator || 'equals';
  return items.filter(item => {
    const itemVal = item?.[field];
    if (itemVal === undefined || itemVal === null) {
      return operator === 'not_equals' || operator === 'not equals';
    }
    const strVal = String(itemVal).toLowerCase();
    const targetVal = String(value || '').toLowerCase();
    switch (operator) {
      case 'equals': return strVal === targetVal;
      case 'not_equals': case 'not equals': return strVal !== targetVal;
      case 'contains': return strVal.includes(targetVal);
      case 'greater_than': case 'greater than': return parseFloat(itemVal) > parseFloat(value);
      case 'less_than': case 'less than': return parseFloat(itemVal) < parseFloat(value);
      case 'exists': return itemVal !== undefined && itemVal !== null && itemVal !== '';
      default: return true;
    }
  });
};

/**
 * Evaluate a user-configured data source to produce a score.
 * @param {object} scanData - Full scan data context
 * @param {object} config - Data source config from dataOverrides[categoryId]
 * @returns {{ status: string, score: number, description: string, metrics: Array }}
 */
export const evaluateDataSourceConfig = (scanData, config) => {
  const { source, path = [], filter, scoring = {} } = config;
  const fallback = { status: 'Unknown', score: 0, description: 'Unable to resolve data source', metrics: [] };

  // 1. Resolve the data at the configured path
  let data = resolveDataPath(scanData, source, path);
  if (data === undefined || data === null) return fallback;

  // 2. Apply filter
  let filteredData = data;
  let totalCount = 0;
  let matchCount = 0;

  if (Array.isArray(data)) {
    totalCount = data.length;
    if (filter && filter.field) {
      filteredData = applyFilter(data, filter);
      matchCount = filteredData.length;
    } else {
      matchCount = totalCount;
    }
  } else if (filter && filter.field === '__leaf__') {
    // Leaf value filtering: compare the resolved primitive value against the filter
    totalCount = 1;
    const leafStr = String(data);
    const filterVal = String(filter.value || '');
    const op = filter.operator || 'equals';
    let leafMatch = false;
    switch (op) {
      case 'equals': leafMatch = leafStr === filterVal; break;
      case 'not_equals': leafMatch = leafStr !== filterVal; break;
      case 'contains': leafMatch = leafStr.toLowerCase().includes(filterVal.toLowerCase()); break;
      case 'not_contains': leafMatch = !leafStr.toLowerCase().includes(filterVal.toLowerCase()); break;
      case 'starts_with': leafMatch = leafStr.toLowerCase().startsWith(filterVal.toLowerCase()); break;
      case 'ends_with': leafMatch = leafStr.toLowerCase().endsWith(filterVal.toLowerCase()); break;
      case 'exists': leafMatch = data !== null && data !== undefined && leafStr !== ''; break;
      case 'is_true': leafMatch = leafStr === 'true' || leafStr === 'True' || data === true; break;
      case 'is_false': leafMatch = leafStr === 'false' || leafStr === 'False' || data === false; break;
      default: leafMatch = leafStr === filterVal;
    }
    matchCount = leafMatch ? 1 : 0;
  }

  // 3. Apply scoring rule
  const { type = 'count', greenThreshold, yellowThreshold, matchValue, description: customDesc } = scoring;

  let status = 'Unknown';
  let score = 0;
  let description = customDesc || '';
  let metrics = [];

  switch (type) {
    case 'count': {
      // Count matching items: >= greenThreshold → Green, >= yellowThreshold → Yellow, else Red
      const green = greenThreshold ?? 1;
      const yellow = yellowThreshold ?? 1;
      if (matchCount >= green) {
        status = 'Green'; score = 100;
        description = description || `${matchCount} matching item(s) found (threshold: ${green})`;
      } else if (matchCount >= yellow) {
        status = 'Yellow'; score = 50;
        description = description || `${matchCount} matching item(s) found`;
      } else {
        status = 'Red'; score = 0;
        description = description || `Only ${matchCount} matching item(s) found`;
      }
      metrics = [{ label: 'Matching', value: matchCount }, { label: 'Total', value: totalCount }];
      break;
    }
    case 'ratio': {
      // Ratio of matching to total: > 80% → Green, > 50% → Yellow, else Red
      const greenPct = greenThreshold ?? 80;
      const yellowPct = yellowThreshold ?? 50;
      const ratio = totalCount > 0 ? (matchCount / totalCount) * 100 : 0;
      if (ratio >= greenPct) {
        status = 'Green'; score = 100;
        description = description || `${Math.round(ratio)}% match rate (${matchCount}/${totalCount})`;
      } else if (ratio >= yellowPct) {
        status = 'Yellow'; score = 50;
        description = description || `${Math.round(ratio)}% match rate (${matchCount}/${totalCount})`;
      } else {
        status = 'Red'; score = 0;
        description = description || `${Math.round(ratio)}% match rate (${matchCount}/${totalCount})`;
      }
      metrics = [{ label: 'Ratio', value: `${Math.round(ratio)}%` }];
      break;
    }
    case 'threshold': {
      // Numeric value: < greenThreshold → Green, < yellowThreshold → Yellow, else Red
      const val = typeof data === 'number' ? data : parseFloat(data);
      const green = greenThreshold ?? 70;
      const yellow = yellowThreshold ?? 85;
      if (isNaN(val)) {
        description = 'Non-numeric value at path';
      } else if (val <= green) {
        status = 'Green'; score = 100;
        description = description || `Value: ${val} (within green threshold ≤${green})`;
      } else if (val <= yellow) {
        status = 'Yellow'; score = 50;
        description = description || `Value: ${val} (within yellow threshold ≤${yellow})`;
      } else {
        status = 'Red'; score = 0;
        description = description || `Value: ${val} (exceeds yellow threshold ${yellow})`;
      }
      metrics = [{ label: 'Value', value: isNaN(val) ? 'N/A' : val }];
      break;
    }
    case 'match': {
      // String match: value matches target → Green, else Red
      const target = (matchValue || '').toLowerCase();
      const actual = Array.isArray(data) 
        ? data.some(v => String(v).toLowerCase().includes(target))
        : String(data).toLowerCase().includes(target);
      if (actual) {
        status = 'Green'; score = 100;
        description = description || `Match found for "${matchValue}"`;
      } else {
        status = 'Red'; score = 0;
        description = description || `No match for "${matchValue}"`;
      }
      break;
    }
    case 'boolean': {
      // Truthy/exists → Green, else Red
      const isTruthy = Array.isArray(data) ? data.length > 0 : !!data;
      if (isTruthy) {
        status = 'Green'; score = 100;
        description = description || 'Data present';
      } else {
        status = 'Red'; score = 0;
        description = description || 'Data not found';
      }
      break;
    }
    default:
      description = `Unknown scoring type: ${type}`;
  }

  // 4. Format description with variable substitution
  const formatDescription = (desc, vars) => {
    if (!desc) return '';
    return desc
      .replace(/{{count}}/g, vars.count ?? '')
      .replace(/{{total}}/g, vars.total ?? '')
      .replace(/{{value}}/g, vars.value ?? '')
      .replace(/{{percent}}/g, vars.percent ?? '');
  };

  const vars = {
    count: matchCount,
    total: totalCount,
    value: (typeof data === 'number' || typeof data === 'string') ? data : '',
    percent: totalCount > 0 ? Math.round((matchCount / totalCount) * 100) + '%' : '0%'
  };

  if (customDesc) {
    description = formatDescription(customDesc, vars);
  }

  return { status, score, description, metrics };
};

/**
 * Get available data source keys from scan data for the UI picker
 */
export const getAvailableDataSources = (scanData) => {
  if (!scanData || typeof scanData !== 'object') return [];
  const rawData = scanData?.data || scanData?.scanData || scanData;
  return Object.keys(rawData).filter(key => 
    key !== '__isPlaceholder__' && key !== '__derived' && !key.startsWith('_')
  );
};

/**
 * Get child keys of a data source for drill-down path selection
 */
export const getChildKeys = (scanData, source, currentPath = []) => {
  const data = resolveDataPath(scanData, source, currentPath);
  if (!data) return [];
  
  if (Array.isArray(data)) {
    // Collect unique keys from ALL objects in the array
    const allKeys = new Set();
    data.forEach(item => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        Object.keys(item).forEach(k => {
          if (!k.startsWith('_')) allKeys.add(k);
        });
      }
    });

    // If it's an array of primitives, it might not have "child keys" in the traditional sense,
    // but maybe it's an array of arrays? We'll stick to objects for now as fields.
    if (allKeys.size > 0) return [...allKeys].sort();
    
    // Fallback: If no object keys found, check if the first element is a basic type (no children)
    return [];
  }
  
  if (data && typeof data === 'object') {
    return Object.keys(data).filter(k => !k.startsWith('_')).sort();
  }
  
  return []; // Leaf value
};

/**
 * Check if the resolved data at a path is an array (needed for filter UI)
 */
export const isArrayAtPath = (scanData, source, pathSegments = []) => {
  const data = resolveDataPath(scanData, source, pathSegments);
  return Array.isArray(data);
};

/**
 * Check if the resolved data at a path is a leaf (primitive) value
 */
export const isLeafAtPath = (scanData, source, pathSegments = []) => {
  if (!pathSegments || pathSegments.length === 0) return false;
  const data = resolveDataPath(scanData, source, pathSegments);
  // It's a leaf if it's not null/undefined, not an array, and not an object
  return data !== null && data !== undefined && !Array.isArray(data) && typeof data !== 'object';
};


/**
 * Default Status descriptions map for Infrastructure Grid
 */
export const getInfrastructureStatusDescription = (findingId, status) => {
    const descriptions = {
      'infrastructure-1': { 'Green': 'Full visibility into all connected network assets', 'Yellow': 'Partial visibility or unknown devices detected', 'Red': 'Critical: Significant number of unmanaged or rogue devices' },
      'infrastructure-2': { 'Green': 'Consistent and reliable workstation lifecycle management', 'Yellow': 'Aging workstations detected; performance may be impacted', 'Red': 'Critical: Significant percentage of workstations out of support/warranty' },
      'infrastructure-3': { 'Green': 'Redundancy present for all critical systems', 'Yellow': 'Partial redundancy (some critical systems not redundant)', 'Red': 'Critical systems with no redundancy' },
      'infrastructure-4': { 'Green': 'Sustained usage below 70% on all critical resources', 'Yellow': 'Sustained usage between 70–85%', 'Red': 'Critical resource usage above 85% or disk free space below 15%' },
      'infrastructure-5': { 'Green': 'Hosts supported, not overcommitted, no hardware alerts', 'Yellow': 'Approaching overcommit thresholds or aging versions', 'Red': 'Unsupported hypervisor versions or active host alerts' },
      'infrastructure-6': { 'Green': 'Core devices supported, patched, stable', 'Yellow': 'Aging firmware or approaching support expiration', 'Red': 'Unsupported firmware or devices showing failure indicators' },
      'infrastructure-7': { 'Green': 'Systems match approved configuration baselines', 'Yellow': 'Minor deviations from baseline', 'Red': 'Significant configuration drift across systems' },
      'infrastructure-8': { 'Green': 'Cloud resources properly sized and redundant', 'Yellow': 'Inefficient sizing or limited redundancy', 'Red': 'Misconfigured or fragile cloud architecture' },
      'infrastructure-9': { 'Green': 'Infrastructure supports projected growth for 12+ months', 'Yellow': 'Growth will stress infrastructure within 6–12 months', 'Red': 'Infrastructure will not support near-term growth' }
    };
    return descriptions[findingId]?.[status] || '';
};

/**
 * Infrastructure Scan Grid Widget Component
 */
export const InfrastructureScanGridWidget = ({ data, config = {}, style = {}, onConfigChange, theme = {} }) => {
  const { title = 'INFRASTRUCTURE', dataOverrides = {} } = config;
  
  const baseFindings = extractInfrastructureFindings(data, config);

  const getStatusDescription = (finding, status) => {
    return getInfrastructureStatusDescription(finding.id, status) || finding.description;
  };

  const categories = config?.categories || DEFAULT_INFRASTRUCTURE_CATEGORIES;
  const baseFindingsMap = baseFindings.reduce((acc, f) => { acc[f.id] = f; return acc; }, {});

  const handleCardClick = (finding) => {
    if (!onConfigChange) return;
    const statusCycle = ['Green', 'Yellow', 'Red', 'Unknown'];
    const currentStatus = finding.status || 'Unknown';
    const nextStatus = statusCycle[(statusCycle.indexOf(currentStatus) + 1) % statusCycle.length];
    
    let nextScore = 0;
    if (nextStatus === 'Green') nextScore = 100;
    else if (nextStatus === 'Yellow') nextScore = 50;

    onConfigChange({
      ...config,
      dataOverrides: {
        ...dataOverrides,
        [finding.id]: {
            status: nextStatus,
            score: nextScore,
            description: getStatusDescription(finding, nextStatus),
            mode: 'manual'
        }
      }
    });
  };

  const infraFindings = categories.map(cat => {
    const override = dataOverrides[cat.id];
    const sourceId = (override && override.autoSourceId) ? override.autoSourceId : cat.id;
    const sourceFinding = baseFindingsMap[sourceId];

    const baseFinding = {
      id: cat.id,
      title: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
      status: sourceFinding ? sourceFinding.status : 'Unknown',
      score: sourceFinding ? sourceFinding.score : 0,
      description: sourceFinding ? sourceFinding.description : 'Configure this item',
      metrics: sourceFinding ? sourceFinding.metrics : [],
      category: 'Infrastructure'
    };

    let finalFinding = { ...baseFinding, isManual: false };

    if (override && (override.status || override.mode === 'manual')) {
      const statusToScore = { 'Green': 100, 'Yellow': 50, 'Red': 0, 'Unknown': 0 };
      finalFinding = {
        ...baseFinding,
        status: override.status,
        score: override.score !== undefined ? override.score : (statusToScore[override.status] || 0),
        description: override.description || getStatusDescription(baseFinding, override.status),
        isManual: true
      };
    } else if (override && override.mode === 'dataSource') {
      const result = evaluateDataSourceConfig(data, override);
      finalFinding = {
        ...baseFinding,
        status: result.status,
        score: result.score,
        description: result.description,
        metrics: result.metrics.length > 0 ? result.metrics : baseFinding.metrics,
        isDataSource: true
      };
    }

    if (override?.description && (!override.mode || override.mode === 'auto')) {
      finalFinding.description = override.description;
    }

    if (override?.displayName) {
      finalFinding.title = override.displayName;
    } else if (cat.label) {
      finalFinding.title = cat.label;
    }

    return finalFinding;
  });

  const calculateAuditScore = () => {
    const knownFindings = infraFindings.filter(f => f.status !== 'Unknown');
    if (knownFindings.length === 0) return 0;
    const scores = knownFindings.map(f => f.score);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Number.isInteger(avgScore) ? avgScore : parseFloat(avgScore.toFixed(2));
  };

  const auditScore = calculateAuditScore();

  return (
    <BaseScanGridWidget
      findings={infraFindings}
      title={title}
      auditScore={auditScore}
      onCardClick={handleCardClick}
      style={style}
      config={config}
      theme={theme}
    />
  );
};

/**
 * Key Findings Advanced Summary Widget (Renamed to match user request)
 * List of all "minor scans" of the 4 Audit Scans.
 * Structure matches Key Findings Summary (List/Table).
 */
export const KeyFindingsAdvancedWidget = ({ data, config = {}, style = {}, theme = {} }) => {
  const { title = 'Key Findings Advanced Summary' } = config;
  
  // Aggregate all findings "minor scans" from the 4 areas
  // 1. Infrastructure (implemented)
  // 2. Managed IT (placeholder/TODO)
  // 3. Security (placeholder/TODO)
  // 4. Cyber Security (placeholder/TODO)
  
  const infrastructureFindings = extractInfrastructureFindings(data, config);
  // We can add placeholders for others if we want the list to look populated?
  // User asked for "list of all minor scans of the 4 Audit Scans".
  // For now I'll include the 9 infrastructure ones.
  const allFindings = [
    ...infrastructureFindings,
    // Add placeholders for other categories to show "intent"
    { id: 'managed-it-1', title: 'Workstation Patch Compliance', status: 'Green', score: 100, description: 'All workstations fully patched', category: 'Managed IT' },
    { id: 'managed-it-2', title: 'Antivirus Definition Status', status: 'Green', score: 100, description: 'Definitions up to date', category: 'Managed IT' },
    { id: 'security-1', title: 'Local Admin Rights Check', status: 'Yellow', score: 50, description: 'Excessive local admins found', category: 'Security' },
    { id: 'cyber-1', title: 'External Vulnerability Scan', status: 'Red', score: 20, description: 'Critical vulnerabilities detected on external IP', category: 'Cyber Security' }
  ];

    const tColors = theme?.statusColors || {};
    const statusColors = {
    'Green': tColors.green || '#10B981', // Emerald 500
    'Yellow': tColors.yellow || '#F59E0B', // Amber 500
    'Red': tColors.red || '#EF4444'     // Red 500
  };

  const statusBgColors = {
    'Green': '#D1FAE5', // Emerald 100
    'Yellow': '#FEF3C7', // Amber 100
    'Red': '#FEE2E2'     // Red 100
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden font-sans" style={style}>
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-100 p-4">
           <h2 className="text-base font-bold text-gray-800 tracking-tight" style={getLabelStyle(config, 'title', {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#1f2937',
            textAlign: 'left'
          }, 'key-findings-advanced')}>
              {title}
           </h2>
           <p className="text-gray-400 text-[10px] mt-0.5">Detailed breakdown of audit findings across all major categories.</p>
      </div>

      {/* List / Table Content */}
      <div className="flex flex-col">
          {allFindings.map((finding, idx) => (
              <div 
                key={finding.id} 
                className={`flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 hover:bg-gray-50 transition-colors ${idx !== allFindings.length - 1 ? 'border-b border-gray-100' : ''}`}
              >
                  {/* Category Pill */}
                  <div className="w-24 hidden sm:block">
                      <span className="text-[8px] uppercase font-bold text-gray-400 tracking-wider bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                          {finding.category || 'General'}
                      </span>
                  </div>

                  {/* Title & Description */}
                  <div className="flex-1 min-w-0">
                      <h4 className="text-[11px] font-bold text-gray-800 flex items-center gap-2">
                        {finding.title} 
                        {/* Mobile Category Pill */}
                        <span className="sm:hidden text-[7px] uppercase font-bold text-gray-400 tracking-wider border border-gray-200 px-1.5 rounded">{finding.category}</span>
                      </h4>
                      <p className="text-[9px] text-gray-500 mt-0.5 truncate">{finding.description}</p>
                  </div>

                  {/* Status */}
                  <div className="flex-shrink-0">
                      <span 
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-[8px] font-bold"
                          style={{ 
                              backgroundColor: statusBgColors[finding.status],
                              color: statusColors[finding.status]
                          }}
                      >
                          {finding.status}
                          <span className="ml-1 text-[8px] opacity-70">
                              {finding.score}%
                          </span>
                      </span>
                  </div>
              </div>
          ))}
      </div>
    </div>
  );
};
